import React, { useState, useEffect } from 'react';
import { 
  CurrencyDollar, 
  Plus, 
  Trash, 
  PencilSimple,
  Receipt,
  X,
  SpinnerGap,
  ChartPie,
  TrendUp,
  Warning,
  CheckCircle,
  Package,
  Wrench,
  FileText,
  UsersThree,
  Users,
  DotsThree,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { jobCostsAPI } from '../api/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const CATEGORY_ICONS = {
  labor: Users,
  materials: Package,
  equipment: Wrench,
  permits: FileText,
  subcontractor: UsersThree,
  other: DotsThree
};

const CATEGORY_COLORS = {
  labor: { bg: 'bg-[#2B4C6F]', text: 'text-[#2B4C6F]', light: 'bg-blue-50' },
  materials: { bg: 'bg-emerald-500', text: 'text-emerald-500', light: 'bg-emerald-50' },
  equipment: { bg: 'bg-amber-500', text: 'text-amber-500', light: 'bg-amber-50' },
  permits: { bg: 'bg-violet-500', text: 'text-violet-500', light: 'bg-violet-50' },
  subcontractor: { bg: 'bg-pink-500', text: 'text-pink-500', light: 'bg-pink-50' },
  other: { bg: 'bg-neutral-500', text: 'text-neutral-500', light: 'bg-neutral-50' }
};

// Add/Edit Cost Modal
const CostEntryModal = ({ 
  isOpen, 
  onClose, 
  jobId, 
  editingCost = null,
  categories = [],
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: 'materials',
    amount: '',
    description: '',
    vendor: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (editingCost) {
      setFormData({
        category: editingCost.category || 'materials',
        amount: editingCost.amount?.toString() || '',
        description: editingCost.description || '',
        vendor: editingCost.vendor || '',
        date: editingCost.date ? new Date(editingCost.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
      });
    } else {
      setFormData({
        category: 'materials',
        amount: '',
        description: '',
        vendor: '',
        date: new Date().toISOString().split('T')[0]
      });
    }
  }, [editingCost, isOpen]);

  const handleSubmit = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Invalid amount', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }
    if (!formData.description.trim()) {
      toast({ title: 'Description required', description: 'Please enter a description', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      if (editingCost) {
        await jobCostsAPI.updateCost(editingCost._id, {
          category: formData.category,
          amount: parseFloat(formData.amount),
          description: formData.description,
          vendor: formData.vendor || null,
          date: formData.date
        });
        toast({ title: 'Cost updated' });
      } else {
        await jobCostsAPI.addCost({
          job_id: jobId,
          category: formData.category,
          amount: parseFloat(formData.amount),
          description: formData.description,
          vendor: formData.vendor || null,
          date: formData.date
        });
        toast({ title: 'Cost added' });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({ 
        title: editingCost ? 'Update failed' : 'Add failed', 
        description: error.response?.data?.detail || 'An error occurred', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
        <DialogHeader>
          <DialogTitle className="text-xl font-black text-neutral-900 dark:text-white flex items-center gap-2">
            <Receipt size={24} weight="bold" className="text-[#2B4C6F]" />
            {editingCost ? 'Edit Cost Entry' : 'Add Cost Entry'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Category */}
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white">Category</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger className="mt-1 h-12 border-2 border-neutral-200 dark:border-neutral-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => {
                  const IconComp = CATEGORY_ICONS[cat.id] || DotsThree;
                  return (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <IconComp size={16} weight="bold" style={{ color: cat.color }} />
                        {cat.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white">Amount ($)</Label>
            <div className="relative mt-1">
              <CurrencyDollar size={20} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="pl-10 h-12 border-2 border-neutral-200 dark:border-neutral-600 text-lg"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What was this expense for?"
              className="mt-1 border-2 border-neutral-200 dark:border-neutral-600 min-h-[80px]"
            />
          </div>

          {/* Vendor */}
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white">Vendor (Optional)</Label>
            <Input
              value={formData.vendor}
              onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
              placeholder="e.g., Home Depot, ABC Supplies"
              className="mt-1 h-12 border-2 border-neutral-200 dark:border-neutral-600"
            />
          </div>

          {/* Date */}
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white">Date</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="mt-1 h-12 border-2 border-neutral-200 dark:border-neutral-600"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-[#2B4C6F] hover:bg-[#203A55] border-2 border-black shadow-[2px_2px_0px_#000]"
          >
            {loading ? (
              <><SpinnerGap size={20} className="animate-spin mr-2" /> Saving...</>
            ) : (
              <>{editingCost ? 'Update' : 'Add'} Cost</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Budget Setting Modal
const BudgetModal = ({ isOpen, onClose, jobId, currentBudget, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [budget, setBudget] = useState(currentBudget?.toString() || '');

  useEffect(() => {
    setBudget(currentBudget?.toString() || '');
  }, [currentBudget, isOpen]);

  const handleSubmit = async () => {
    if (!budget || parseFloat(budget) <= 0) {
      toast({ title: 'Invalid budget', description: 'Please enter a valid budget amount', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await jobCostsAPI.setBudget(jobId, { estimated_budget: parseFloat(budget) });
      toast({ title: 'Budget updated' });
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({ title: 'Update failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000]">
        <DialogHeader>
          <DialogTitle className="text-xl font-black">Set Budget</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Label className="font-bold">Estimated Budget ($)</Label>
          <div className="relative mt-1">
            <CurrencyDollar size={20} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              type="number"
              step="0.01"
              min="0"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="10000.00"
              className="pl-10 h-12 border-2 text-lg"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[#2B4C6F]">
            {loading ? <SpinnerGap size={20} className="animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Cost Tracking Component
const JobCostTracker = ({ jobId }) => {
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [costsRes, categoriesRes] = await Promise.all([
        jobCostsAPI.getJobCosts(jobId),
        jobCostsAPI.getCategories()
      ]);

      if (costsRes.data.success) {
        setCosts(costsRes.data.costs);
        setSummary(costsRes.data.summary);
      }
      if (categoriesRes.data.success) {
        setCategories(categoriesRes.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch costs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [jobId]);

  const handleDelete = async (costId) => {
    if (!confirm('Delete this cost entry?')) return;
    
    try {
      await jobCostsAPI.deleteCost(costId);
      toast({ title: 'Cost deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const filteredCosts = filterCategory === 'all' 
    ? costs 
    : costs.filter(c => c.category === filterCategory);

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000]">
        <div className="flex items-center justify-center py-8">
          <SpinnerGap size={32} weight="bold" className="animate-spin text-[#2B4C6F]" />
        </div>
      </div>
    );
  }

  const percentUsed = summary?.percent_used || 0;
  const isOverBudget = summary?.total_spent > summary?.estimated_budget && summary?.estimated_budget > 0;

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 bg-gradient-to-r from-[#2B4C6F] to-blue-600 text-white cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <ChartPie size={24} weight="bold" />
            </div>
            <div>
              <h3 className="text-lg font-black">Cost Tracking</h3>
              <p className="text-sm text-white/80">
                ${(summary?.total_spent || 0).toLocaleString()} spent
                {summary?.estimated_budget > 0 && ` of $${summary.estimated_budget.toLocaleString()}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isOverBudget && (
              <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                <Warning size={14} weight="bold" /> Over Budget
              </div>
            )}
            {expanded ? <CaretUp size={20} weight="bold" /> : <CaretDown size={20} weight="bold" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-5">
          {/* Budget Overview */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-neutral-600 dark:text-neutral-400">Budget Progress</span>
              <button 
                onClick={() => setShowBudgetModal(true)}
                className="text-sm text-[#2B4C6F] hover:underline font-bold"
              >
                {summary?.estimated_budget > 0 ? 'Edit Budget' : 'Set Budget'}
              </button>
            </div>
            <div className="h-4 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden border-2 border-neutral-200 dark:border-neutral-600">
              <div 
                className={`h-full transition-all ${isOverBudget ? 'bg-red-500' : 'bg-[#2B4C6F]'}`}
                style={{ width: `${Math.min(percentUsed, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span className={`font-bold ${isOverBudget ? 'text-red-500' : 'text-neutral-900 dark:text-white'}`}>
                ${(summary?.total_spent || 0).toLocaleString()} spent
              </span>
              <span className="text-neutral-500">
                {summary?.estimated_budget > 0 
                  ? `$${(summary?.remaining || 0).toLocaleString()} remaining`
                  : 'No budget set'
                }
              </span>
            </div>
          </div>

          {/* Category Breakdown */}
          {Object.keys(summary?.by_category || {}).length > 0 && (
            <div className="mb-6 grid grid-cols-3 gap-2">
              {categories.filter(cat => summary?.by_category?.[cat.id] > 0).map(cat => {
                const amount = summary?.by_category?.[cat.id] || 0;
                const IconComp = CATEGORY_ICONS[cat.id] || DotsThree;
                const colors = CATEGORY_COLORS[cat.id] || CATEGORY_COLORS.other;
                return (
                  <div 
                    key={cat.id}
                    className={`p-3 rounded-lg ${colors.light} dark:bg-neutral-700 border-2 border-neutral-200 dark:border-neutral-600`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <IconComp size={16} weight="bold" className={colors.text} />
                      <span className="text-xs font-bold text-neutral-600 dark:text-neutral-300">{cat.label}</span>
                    </div>
                    <span className="text-lg font-black text-neutral-900 dark:text-white">
                      ${amount.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[140px] h-9 border-2 border-neutral-200 dark:border-neutral-600">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-neutral-500">
                {filteredCosts.length} entries
              </span>
            </div>
            <Button
              onClick={() => { setEditingCost(null); setShowAddModal(true); }}
              className="bg-[#2B4C6F] hover:bg-[#203A55] border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none"
              data-testid="add-cost-btn"
            >
              <Plus size={18} weight="bold" className="mr-1" />
              Add Cost
            </Button>
          </div>

          {/* Cost List */}
          {filteredCosts.length === 0 ? (
            <div className="text-center py-8 bg-neutral-50 dark:bg-neutral-700 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-600">
              <Receipt size={48} weight="light" className="mx-auto text-neutral-300 dark:text-neutral-500 mb-2" />
              <p className="text-neutral-500 dark:text-neutral-400">No costs recorded yet</p>
              <Button 
                onClick={() => setShowAddModal(true)}
                variant="outline"
                className="mt-3"
              >
                <Plus size={16} weight="bold" className="mr-1" /> Add First Cost
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredCosts.map(cost => {
                const cat = categories.find(c => c.id === cost.category) || { label: 'Other', color: '#6b7280' };
                const IconComp = CATEGORY_ICONS[cost.category] || DotsThree;
                const colors = CATEGORY_COLORS[cost.category] || CATEGORY_COLORS.other;
                
                return (
                  <div 
                    key={cost._id}
                    className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg border-2 border-neutral-200 dark:border-neutral-600 hover:border-[#2B4C6F] transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
                        <IconComp size={20} weight="bold" className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-neutral-900 dark:text-white">{cost.description}</p>
                        <p className="text-xs text-neutral-500">
                          {cat.label} • {new Date(cost.date || cost.created_at).toLocaleDateString()}
                          {cost.vendor && ` • ${cost.vendor}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-black text-lg text-neutral-900 dark:text-white">
                        ${cost.amount.toLocaleString()}
                      </span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => { setEditingCost(cost); setShowAddModal(true); }}
                          className="p-2 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-lg"
                          title="Edit"
                        >
                          <PencilSimple size={16} weight="bold" className="text-neutral-600 dark:text-neutral-300" />
                        </button>
                        <button
                          onClick={() => handleDelete(cost._id)}
                          className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                          title="Delete"
                        >
                          <Trash size={16} weight="bold" className="text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CostEntryModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingCost(null); }}
        jobId={jobId}
        editingCost={editingCost}
        categories={categories}
        onSuccess={fetchData}
      />
      <BudgetModal
        isOpen={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        jobId={jobId}
        currentBudget={summary?.estimated_budget}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default JobCostTracker;
